import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';

/**
 * Spog pass-through endpoint for recommendation status transitions. The
 * recommendation-service's `OFFER_ACCEPTED` audience-status API lives behind
 * this URL — see spec 003/002 for the pass-through contract. Must match
 * both spog#10763's route AND recommendation-service's native path; without
 * the `/update-status` suffix every Accept would 404 and the user would
 * see the generic retry copy on a request that could never succeed.
 */
export const EVERCYCLE_UPDATE_STATUS_URL = '/rest/v1/recommendations/update-status';

/**
 * The audience the GUI acts as — M1 only exposes acceptance to the customer
 * audience (partner acceptance is explicitly out of scope).
 */
export const EVERCYCLE_ACCEPT_AUDIENCE = 'CUSTOMER' as const;

export interface EvercycleAcceptRequest {
    readonly recommendation_id: string;
    readonly audience: typeof EVERCYCLE_ACCEPT_AUDIENCE;
    readonly status: 'OFFER_ACCEPTED';
    readonly offer_id: string;
    readonly status_note?: string;
}

/**
 * Narrow outcome vocabulary the dialog switches on. Each outcome maps 1:1 to
 * a user-visible path spelled out in spec 003/003 — the mapping lives on the
 * boundary (this service) so the dialog never inspects HTTP primitives.
 */
export type EvercycleAcceptOutcome =
    | { kind: 'ACCEPTED' }
    | { kind: 'OFFER_MISMATCH' }
    | { kind: 'OFFER_EXPIRED' }
    | { kind: 'NOT_OPEN' }
    | { kind: 'NOT_ACTIVE' }
    | { kind: 'UNAUTHORIZED' }
    | { kind: 'SERVER_ERROR' };

/**
 * POSTs the acceptance to spog's update-status endpoint and normalises the
 * HTTP response into an `EvercycleAcceptOutcome`. All non-200 paths are
 * surfaced as values (never as thrown errors) so the dialog's error-handling
 * code is a single switch rather than a try/catch dance.
 *
 * The service deliberately does NOT emit the Amplitude event — the dialog
 * owns the event because it also owns the `total_amount_bucket` the event
 * needs, which is derived from the valuation on the active render-info.
 */
@Injectable({ providedIn: 'root' })
export class EvercycleAcceptService {
    private readonly http = inject(HttpClient);

    accept(request: EvercycleAcceptRequest): Observable<EvercycleAcceptOutcome> {
        return this.http.post(EVERCYCLE_UPDATE_STATUS_URL, request, { observe: 'response' }).pipe(
            map((): EvercycleAcceptOutcome => ({ kind: 'ACCEPTED' })),
            catchError((err: HttpErrorResponse) => of<EvercycleAcceptOutcome>(mapError(err))),
        );
    }
}

/**
 * HTTP → outcome mapping, kept as a free function so unit tests can exercise
 * each branch without spinning up `HttpTestingController`. Spec 003/003
 * defines the exact mapping; keep this in sync with the spog contract.
 */
export function mapError(err: HttpErrorResponse): EvercycleAcceptOutcome {
    if (err.status === 403) {
        return { kind: 'UNAUTHORIZED' };
    }
    if (err.status >= 500) {
        return { kind: 'SERVER_ERROR' };
    }
    if (err.status === 400) {
        const code = extractErrorCode(err);
        switch (code) {
            case 'OFFER_MISMATCH':
                return { kind: 'OFFER_MISMATCH' };
            case 'OFFER_EXPIRED':
            // `NO_VALUATION` from recommendation-service (passed through
            // untranslated by spog) means the server no longer holds a
            // valuation for this recommendation. User-facing it's the same
            // outcome as OFFER_EXPIRED: a new valuation will be issued,
            // retrying with this offer_id cannot succeed.
            // eslint-disable-next-line no-fallthrough
            case 'NO_VALUATION':
                return { kind: 'OFFER_EXPIRED' };
            case 'NOT_OPEN':
                return { kind: 'NOT_OPEN' };
            case 'NOT_ACTIVE':
                return { kind: 'NOT_ACTIVE' };
            default:
                // An unknown 400 is treated as a server error so the user is
                // offered a retry rather than silently closing the dialog.
                return { kind: 'SERVER_ERROR' };
        }
    }
    // Network errors / 0 status fall under SERVER_ERROR for retry UX.
    return { kind: 'SERVER_ERROR' };
}

function extractErrorCode(err: HttpErrorResponse): string | undefined {
    const body = err.error as { code?: unknown; error?: { code?: unknown } } | null;
    if (!body) {
        return undefined;
    }
    if (typeof body.code === 'string') {
        return body.code;
    }
    if (body.error && typeof body.error.code === 'string') {
        return body.error.code;
    }
    return undefined;
}
